import { useProductStore } from '@/stores/productsStore';
import { evaluate, explain, isNoList } from '@/services/pricingRules';

// Lines that never get a rule badge: MISC, TIP, BSUR, offline manual entries.
export function isRuleEligibleLine(line) {
  if (!line) return false;
  if (line.IsOfflineManualEntry) return false;
  const sku = String(line.ItemId || line.Sku || '');
  const retail = String(line.RetailSku || '');
  if (sku === '2' || sku === 'MISC-2' || sku === 'TIP-99') return false;
  if (sku.toUpperCase().startsWith('BSUR') || retail.toUpperCase().startsWith('BSUR')) return false;
  if (sku.startsWith('MANUAL-')) return false;
  return true;
}

// sku -> product, rebuilt whenever the catalogue array identity or size changes.
let indexFor = null;
let indexSize = -1;
let skuIndex = new Map();

function catalogueProduct(store, sku) {
  const products = store.products;
  if (indexFor !== products || indexSize !== products.length) {
    skuIndex = new Map();
    for (const p of products) if (p && p.Sku && !p._deleted) skuIndex.set(String(p.Sku), p);
    indexFor = products;
    indexSize = products.length;
  }
  return skuIndex.get(String(sku)) || null;
}

// The barcode cache (server-fetched products) is re-parsed only when its stored string
// changes, so a product cached by a scan a moment ago is visible immediately.
let cacheRaw = null;
let cacheValues = [];
function cachedProduct(sku) {
  let raw = null;
  try { raw = localStorage.getItem('cachedProducts'); } catch { raw = null; }
  if (raw !== cacheRaw) {
    try {
      cacheValues = raw ? Object.values(JSON.parse(raw)) : [];
    } catch {
      cacheValues = [];
    }
    cacheRaw = raw;
  }
  return cacheValues.find(p => p && !p._deleted && String(p.Sku) === String(sku)) || null;
}

const memo = new Map();

// One shared minute clock so badges appear/disappear at rule boundaries without a reload.
let clockStarted = false;
function ensureClock(store) {
  if (clockStarted || typeof setInterval !== 'function') return;
  clockStarted = true;
  setInterval(() => store.tickRulesClock(), 60 * 1000);
}

/**
 * Resolve the product behind a cart line (by SKU, from the catalogue file first, then the
 * barcode cache) and evaluate the branch's rule set against it. Never reads line data for
 * selectors, so it works for every way a line can be created. Memoised per
 * (sku, price, rulesVersion, minute) so a 36k-item catalogue is never scanned per render.
 */
export function evaluateLineRule(line, store = useProductStore()) {
  if (!isRuleEligibleLine(line)) return null;
  if (!store.pricingRulesEnabled) return null;
  ensureClock(store);
  const sku = String(line.ItemId || line.Sku || '');
  const price = Number(line.OriginalPrice ?? line.Price) || 0;
  if (!sku || price <= 0) return null;

  const tick = store.rulesClock;
  const key = `${sku}|${price}|${store.rulesVersion}|${tick}`;
  if (memo.has(key)) return memo.get(key);
  if (memo.size > 2000) memo.clear();

  // Catalogue file first, then the barcode cache, then the selectors captured on the line
  // itself when it was added (survives a cache clear and a page reload).
  let product = catalogueProduct(store, sku) || cachedProduct(sku);
  if (!product && line.ruleSelectors) {
    product = {
      Sku: sku,
      Brand: line.ruleSelectors.Brand,
      Categories: line.ruleSelectors.Categories,
      PriceList: line.ruleSelectors.PriceList,
      MaxQty: line.ruleSelectors.MaxQty,
    };
  }
  if (!product) {
    // Not memoised: the product may show up a moment later (online scan writing the cache).
    return null;
  }
  const match = evaluateProductRule(product, price, store);
  memo.set(key, match);
  return match;
}

/** The evaluator input for a catalogue/cache product at the given base price. */
export function productContext(product, price, store = useProductStore()) {
  const lists = store.branchPriceLists || [];
  const primary = lists[0] || '';
  const priceList = !isNoList(product.PriceList) ? product.PriceList : primary;
  // Secondary-priced rows carry a placeholder quantity (99), which is not stock: skip the threshold.
  const fromPrimary = !isNoList(priceList) && !isNoList(primary) && String(priceList).trim().toLowerCase() === String(primary).trim().toLowerCase();
  const qoh = fromPrimary && Number.isFinite(Number(product.MaxQty)) ? Number(product.MaxQty) : null;
  return {
    sku: product.Sku,
    brand: product.Brand,
    categories: Array.isArray(product.Categories) ? product.Categories : [],
    priceList,
    price: Number(price) || 0,
    qoh,
  };
}

/** Evaluate a catalogue/cache product object at the given base price. */
export function evaluateProductRule(product, price, store = useProductStore()) {
  if (!product || !store.pricingRulesEnabled) return null;
  return evaluate(productContext(product, price, store), store.pricingRules, store.rulesClock, { inclusiveEndDate: store.inclusiveEndDate });
}

/** Resolve the product behind a cart line the same way evaluateLineRule does. */
function resolveLineProduct(line, store) {
  const sku = String(line.ItemId || line.Sku || '');
  let product = catalogueProduct(store, sku) || cachedProduct(sku);
  if (!product && line.ruleSelectors) {
    product = { Sku: sku, Brand: line.ruleSelectors.Brand, Categories: line.ruleSelectors.Categories, PriceList: line.ruleSelectors.PriceList, MaxQty: line.ruleSelectors.MaxQty };
  }
  return product;
}

/**
 * Full breakdown for the details popup: the product as evaluated, the branch's lists, the
 * selected rule and every candidate with the reason it was or was not chosen. Not memoised.
 */
export function explainLineRule(line, store = useProductStore()) {
  const empty = { eligible: false, product: null, context: null, branchLists: store.branchPriceLists || [], match: null, candidates: [], rulesOnFile: store.pricingRules.length };
  if (!isRuleEligibleLine(line) || !store.pricingRulesEnabled) return empty;
  const price = Number(line.OriginalPrice ?? line.Price) || 0;
  const product = resolveLineProduct(line, store);
  if (!product) return { ...empty, eligible: true };
  const context = productContext(product, price, store);
  const { match, candidates } = explain(context, store.pricingRules, store.rulesClock, { inclusiveEndDate: store.inclusiveEndDate });
  return { eligible: true, product, context, branchLists: store.branchPriceLists || [], match, candidates, rulesOnFile: store.pricingRules.length };
}
